import { useNavigate } from "react-router-dom";
import { Button, SubmitButton, useSubmittingMutation } from "@/design-system/components/ui";
import { portalAuthApi } from "../api/auth";
import { usePortalAuth } from "../PortalAuthContext";
import type { PreApprovalCelebrationVariant } from "../api/types";

// Sender-Fields v1 — one-time celebratory "you've been pre-approved" popup.
// Renders only while /me flags showPreApprovalCelebration; the primary button
// navigates to the item, the secondary dismisses. Both acknowledge the item
// server-side (POST /auth/me/preapproval-celebration-seen) and refetch /me so
// it won't show again. Conditional pre-approval — no implication of
// final/committed approval.
//
// Approval chains (D19, ruling Q7) — `preApprovalCelebrationVariant` picks the
// copy. "BorrowerIssued" / "SellerReady" are the Appendix E copy for chain
// items; "LegacyBuyer" / "LegacySeller" — and a null variant from an older
// server, which falls back to `preApprovalCelebrationIsSeller` — keep the copy
// in-flight items were issued with, verbatim.

interface CelebrationCopy {
  ariaLabel: string;
  title: string;
  body: string;
  cta: string;
  later: string;
}

export function celebrationCopy(
  variant: PreApprovalCelebrationVariant | null,
  isSeller: boolean,
  businessName: string,
  sellerName: string,
): CelebrationCopy {
  if (variant === "BorrowerIssued") {
    const title = "Your Conditional Loan Approval has been issued";
    return {
      ariaLabel: title,
      title,
      body: `${sellerName}, as Note Holder, has issued a Borrower Conditional Loan Approval for your acquisition of ${businessName}. It is conditional and is not a commitment to lend. Please review and sign it.`,
      cta: "Review and sign",
      later: "Later",
    };
  }
  if (variant === "SellerReady") {
    const title = "Your Seller Conditional Approval is ready";
    return {
      ariaLabel: title,
      title,
      body: `Your Seller Conditional Approval and the Borrower Conditional Loan Approval for ${businessName} are ready for your review and signature.`,
      cta: "Review and sign",
      later: "Later",
    };
  }
  const legacySeller = variant === "LegacySeller" || (variant !== "LegacyBuyer" && isSeller);
  return {
    ariaLabel: "You've been pre-approved",
    title: "Congratulations — you've been pre-approved",
    body: legacySeller
      ? `Phoenix Capital Solutions, LLC has issued your Conditional Pre-Approval for your seller-financed note on ${businessName}. Your letter lays out the preliminary terms, your estimated monthly distribution, and the steps toward closing. Review it, print a copy for your records, and sign whenever you're ready.`
      : `Phoenix Capital Solutions, LLC has issued your Conditional Pre-Approval for the acquisition of ${businessName}. Your letter lays out your estimated terms, your monthly payment, and the steps toward closing. Review it, print a copy for your records, and sign whenever you're ready — there's no rush.`,
    cta: "Review my letter",
    later: "I'll review later",
  };
}

export function PreApprovalCelebrationModal() {
  const auth = usePortalAuth();
  const navigate = useNavigate();

  const itemId = auth.me?.preApprovalCelebrationItemId ?? null;
  const packageId = auth.me?.preApprovalCelebrationPackageId ?? null;

  const ack = useSubmittingMutation(
    async (review: boolean) => {
      if (itemId) {
        try {
          await portalAuthApi.acknowledgePreApprovalCelebration(itemId);
        } catch {
          // Non-fatal — the refresh below runs regardless so a transient
          // ack failure doesn't trap the user behind the modal.
        }
      }
      return review;
    },
    {
      refresh: auth.refresh,
      onSuccess: (review) => {
        if (review && packageId && itemId) {
          navigate(`/portal/packages/${packageId}/items/${itemId}`);
        }
      },
    },
  );
  const busy = ack.pending;

  if (!auth.me?.showPreApprovalCelebration || !itemId) return null;

  const businessName = auth.me.preApprovalCelebrationBusinessName?.trim() || "your business";
  const copy = celebrationCopy(
    auth.me.preApprovalCelebrationVariant ?? null,
    auth.me.preApprovalCelebrationIsSeller === true,
    businessName,
    auth.me.preApprovalCelebrationSellerName?.trim() || "The seller",
  );

  function reviewNow() {
    void ack.submit(true);
  }

  function later() {
    void ack.submit(false);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-onyx/50 p-space-4"
      data-testid="preapproval-celebration"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={copy.ariaLabel}
        className="max-w-lg w-full rounded-md bg-white-pure p-space-6 shadow-lg border-t-[2px] border-t-gold text-center"
      >
        <h2 className="font-display font-bold text-h3 text-onyx m-0 uppercase tracking-wide break-words max-w-full">
          {copy.title}
        </h2>
        <p className="text-body text-gray-mid mt-space-3 max-w-reading mx-auto">{copy.body}</p>
        <div className="mt-space-5 flex justify-center gap-space-3 flex-wrap">
          <Button
            type="button"
            variant="secondary"
            onClick={later}
            disabled={busy}
            data-testid="preapproval-celebration-later"
          >
            {copy.later}
          </Button>
          <SubmitButton
            type="button"
            onClick={reviewNow}
            pending={busy}
            pendingLabel="…"
            data-testid="preapproval-celebration-cta"
          >
            {copy.cta}
          </SubmitButton>
        </div>
      </div>
    </div>
  );
}
